import React, { useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import Hls from 'hls.js';

export type ToCanvasFn = (normX: number, normY: number) => [number, number];

export interface VideoPlayerHandle {
    getVideoElement: () => HTMLVideoElement | null;
    getCurrentFrame: () => number;
}

interface VideoPlayerProps {
    videoUrl: string;
    fps: number;
    onFrameChange?: (frame: number) => void;
    drawOverlay?: (ctx: CanvasRenderingContext2D, toCanvas: ToCanvasFn) => void;
}

const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(
    ({ videoUrl, fps, onFrameChange, drawOverlay }, ref) => {
        const videoRef = useRef<HTMLVideoElement>(null);
        const canvasRef = useRef<HTMLCanvasElement>(null);
        const frameRef = useRef(0);
        const drawOverlayRef = useRef(drawOverlay);
        const onFrameChangeRef = useRef(onFrameChange);
        const [speed, setSpeed] = useState(1);

        const SPEEDS = [0.5, 1, 2, 4, 8, 16];

        drawOverlayRef.current = drawOverlay;
        onFrameChangeRef.current = onFrameChange;

        useImperativeHandle(ref, () => ({
            getVideoElement: () => videoRef.current,
            getCurrentFrame: () => frameRef.current,
        }));

        const rafRef = useRef(0);
        const lastFrameRef = useRef(-1);

        const redraw = useCallback(() => {
            const canvas = canvasRef.current;
            const video = videoRef.current;
            const overlay = drawOverlayRef.current;
            if (!canvas || !video || !overlay) return;

            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const videoRect = video.getBoundingClientRect();
            const videoWidth = video.videoWidth;
            const videoHeight = video.videoHeight;
            if (!videoWidth || !videoHeight) return;

            const containerAspect = videoRect.width / videoRect.height;
            const videoAspect = videoWidth / videoHeight;

            let displayWidth: number, displayHeight: number, offsetX: number, offsetY: number;
            if (containerAspect > videoAspect) {
                displayHeight = videoRect.height;
                displayWidth = displayHeight * videoAspect;
                offsetX = (videoRect.width - displayWidth) / 2;
                offsetY = 0;
            } else {
                displayWidth = videoRect.width;
                displayHeight = displayWidth / videoAspect;
                offsetX = 0;
                offsetY = (videoRect.height - displayHeight) / 2;
            }

            canvas.width = videoRect.width;
            canvas.height = videoRect.height;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const toCanvas: ToCanvasFn = (normX, normY) => [
                offsetX + normX * displayWidth,
                offsetY + normY * displayHeight,
            ];

            overlay(ctx, toCanvas);
        }, []);

        const tick = useCallback(() => {
            const video = videoRef.current;
            if (!video) return;
            const frame = Math.floor(video.currentTime * fps);
            if (frame !== lastFrameRef.current) {
                lastFrameRef.current = frame;
                frameRef.current = frame;
                onFrameChangeRef.current?.(frame);
            }
            redraw();
            rafRef.current = requestAnimationFrame(tick);
        }, [fps, redraw]);

        useEffect(() => {
            const video = videoRef.current;
            if (!video || !fps) return;

            const startLoop = () => {
                if (!rafRef.current) rafRef.current = requestAnimationFrame(tick);
            };
            const stopLoop = () => {
                if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
                const frame = Math.floor(video.currentTime * fps);
                frameRef.current = frame;
                onFrameChangeRef.current?.(frame);
                redraw();
            };

            const onSeeked = () => {
                const frame = Math.floor(video.currentTime * fps);
                lastFrameRef.current = -1;
                frameRef.current = frame;
                onFrameChangeRef.current?.(frame);
                redraw();
                if (!video.paused) startLoop();
            };

            video.addEventListener('play', startLoop);
            video.addEventListener('pause', stopLoop);
            video.addEventListener('seeked', onSeeked);

            if (!video.paused) startLoop();

            return () => {
                video.removeEventListener('play', startLoop);
                video.removeEventListener('pause', stopLoop);
                video.removeEventListener('seeked', onSeeked);
                if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
            };
        }, [fps, tick, redraw]);

        useEffect(() => {
            const video = videoRef.current;
            if (!video) return;

            const handleLoadedMetadata = () => redraw();
            const handleResize = () => redraw();

            video.addEventListener('loadedmetadata', handleLoadedMetadata);
            window.addEventListener('resize', handleResize);

            if (video.videoWidth && video.videoHeight) redraw();

            return () => {
                video.removeEventListener('loadedmetadata', handleLoadedMetadata);
                window.removeEventListener('resize', handleResize);
            };
        }, [redraw]);

        const hlsRef = useRef<Hls | null>(null);

        useEffect(() => {
            const video = videoRef.current;
            if (!video || !videoUrl) return;

            if (videoUrl.endsWith('.m3u8') && Hls.isSupported()) {
                const hls = new Hls();
                hls.loadSource(videoUrl);
                hls.attachMedia(video);
                hlsRef.current = hls;
                return () => { hls.destroy(); hlsRef.current = null; };
            } else {
                video.src = videoUrl;
            }
        }, [videoUrl]);

        const changeSpeed = useCallback((newSpeed: number) => {
            setSpeed(newSpeed);
            if (videoRef.current) videoRef.current.playbackRate = newSpeed;
        }, []);

        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#000' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                    <video
                        ref={videoRef}
                        controls
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                    <canvas
                        ref={canvasRef}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                        }}
                    />
                </div>
                <div style={{ display: 'flex', gap: '4px', padding: '6px 8px', backgroundColor: '#222' }}>
                    {SPEEDS.map((s) => (
                        <button
                            key={s}
                            onClick={() => changeSpeed(s)}
                            style={{
                                padding: '4px 10px',
                                fontSize: '12px',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                backgroundColor: speed === s ? '#007bff' : '#444',
                                color: '#fff',
                            }}
                        >
                            {s}x
                        </button>
                    ))}
                </div>
            </div>
        );
    }
);

export default VideoPlayer;
